import styled from 'styled-components';
import { Avatar, IconName, Link, Position, Stack, StackItem, Tag, Tooltip, Typography } from '@optimus-web/core';
import { LocalizedText, lt } from '@mews/framework';
import {
    AssistantReservationTimeState,
    AssistantSummaryReservationResult,
    Customer,
} from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { getFullName } from '@mews-commander/utils';
import { useCustomerReservationsQuery } from '../../query/useCustomerReservations';
import { useCustomerImageUrl } from '../hooks/useCustomerImageUrl';

const TooltipWrapper = Tooltip(styled.span`
    display: inline-block;
    position: relative;
`);

interface CustomerInfoProps {
    customer: Customer;
}

const getNumberOfReservations = (
    reservationResult: AssistantSummaryReservationResult,
    timeState: AssistantReservationTimeState
): number | undefined =>
    reservationResult?.reservations?.filter((reservation) => reservation.timeState === timeState)?.length;

interface LocalizedVisitsProps {
    textKey: Mews.TextKey;
    numberOfVisits: number | undefined;
}

const LocalizedVisits = ({ textKey, numberOfVisits }: LocalizedVisitsProps) => (
    <div>{!!numberOfVisits && <LocalizedText textKey={textKey} substitutes={{ numberOfVisits }} />}</div>
);

export const CustomerInfo = ({ customer }: CustomerInfoProps) => {
    const { data: reservationResult } = useCustomerReservationsQuery(customer.id);
    const imageSrc = useCustomerImageUrl(customer);
    if (!customer) return null;

    const fullName = getFullName(customer);
    const { email } = customer;
    const phone = customer.telephone;
    const isVip = customer.customerClassifications.veryImportant;

    const visits = reservationResult?.reservations?.length;

    let tooltip = <></>;
    if (visits) {
        const pastVisits = getNumberOfReservations(reservationResult, AssistantReservationTimeState.Past);
        const currentVisits = getNumberOfReservations(reservationResult, AssistantReservationTimeState.CheckedIn);
        const futureVisits = getNumberOfReservations(reservationResult, AssistantReservationTimeState.Future);
        tooltip = (
            <>
                <LocalizedVisits numberOfVisits={pastVisits} textKey={TextKey.AssistantCustomerSummaryPastVisits} />
                <LocalizedVisits
                    numberOfVisits={currentVisits}
                    textKey={TextKey.AssistantCustomerSummaryCurrentVisits}
                />
                <LocalizedVisits numberOfVisits={futureVisits} textKey={TextKey.AssistantCustomerSummaryFutureVisits} />
            </>
        );
    }

    return (
        <Stack vertical={false} spacing="300">
            <StackItem>
                <Avatar title={fullName} src={imageSrc} />
            </StackItem>
            <Stack vertical spacing="100">
                <Link strong underline={false}>
                    {email}
                </Link>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {phone}
                </Typography>
                <Stack vertical={false} spacing="100">
                    {isVip && <Tag>{lt('VIP')}</Tag>}
                    {!!visits && (
                        <TooltipWrapper tooltip={tooltip} tooltipPosition={Position.Top}>
                            <Tag variant="basicSubtle" leadingIcon={IconName.Restore}>
                                {lt('AssistantCustomerSummaryReturning')}
                            </Tag>
                        </TooltipWrapper>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};
